<template>
  <div class="container-fluid pt-4">

    <!-- Header -->
    <div class="row mb-3">
      <div class="col-md-6 col-sm-12">
        <h3 class="text-primary">Inventory Activity</h3>
      </div>
    </div>

    <!-- Tabs -->
    <ul class="nav nav-tabs mb-3">
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'movements' }" href="#" @click.prevent="activeTab = 'movements'">
          Stock Movements
        </a>
      </li>
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'changes' }" href="#" @click.prevent="activeTab = 'changes'">
          Product Changes
        </a>
      </li>
    </ul>

    <!-- Filters -->
    <div class="row mb-3">
      <div class="col-md-3 col-sm-12">
        <input class="form-control" placeholder="Item name or activity" v-model="filters.search" />
      </div>
      <div class="col-md-3 col-sm-12" v-if="activeTab === 'movements'">
        <select class="form-control" v-model="filters.activity">
          <option value="">All Activities</option>
          <option value="sold">Sale</option>
          <option value="transfer">Transfer</option>
          <option value="delete">Delete</option>
          <option value="issue">Issue</option>
          <option value="return">Return</option>
          <option value="restock">Restock</option>
        </select>
      </div>
      <div class="col-md-3 col-sm-12">
        <date-range-picker
          :auto-apply="autoApply"
          v-model="dateRange"
          :ranges="ranges"
          :opens="datepicker_opens"
          :append-to-body="append_to_body"
          class="custom-width-datepicker pb-2"
        >
          <template #input="picker" style="width: 100% !important;">
            <p v-if="picker.startDate && picker.endDate">
              {{ formatDate(picker.startDate, 'DD-MMM-YYYY') }} - {{ formatDate(picker.endDate, 'DD-MMM-YYYY') }}
            </p>
            <p v-else><i class="bi bi-calendar-event"></i> Filter by Date</p>
          </template>
        </date-range-picker>
      </div>
      <div class="col-md-2 col-sm-12">
        <button class="btn btn-primary w-100" @click="applyFilters">Filter</button>
      </div>
    </div>

    <!-- Stock Movements Tab -->
    <div v-if="activeTab === 'movements'" style="margin-bottom: 5em;">
      <div v-if="movementsLoaded">
        <div v-if="items.length > 0">
          <hr />

          <!-- KPI Cards -->
          <div v-if="allSoldQty > 0" class="row text-center mb-2">
            <div class="col-md-3 col-sm-6 border-right border-dark">
              <h5>{{ allSoldQty }}</h5>
              <p>Units Sold</p>
            </div>
            <div class="col-md-3 col-sm-6 border-right border-dark">
              <h5>{{ convertCurrencyToShillings(allSoldValue) }}</h5>
              <p>Sales Value</p>
            </div>
            <div class="col-md-3 col-sm-6 border-right border-dark">
              <h5>{{ convertCurrencyToShillings(allSoldProfit) }}</h5>
              <p>Profit</p>
            </div>
            <div class="col-md-3 col-sm-6">
              <h5>{{ convertCurrencyToShillings(avgSellingPrice) }}</h5>
              <p>Avg Unit Price</p>
            </div>
          </div>
          <hr />

          <!-- Sales Trend Chart -->
          <div v-if="hasSalesGraph" class="mb-4">
            <div class="d-flex align-items-center mb-2">
              <strong class="mr-3">Sales Trend</strong>
              <div class="btn-group btn-group-sm" role="group">
                <button
                  type="button"
                  class="btn"
                  :class="trendMetric === 'qty' ? 'btn-primary' : 'btn-outline-secondary'"
                  @click="trendMetric = 'qty'"
                >Qty</button>
                <button
                  type="button"
                  class="btn"
                  :class="trendMetric === 'value' ? 'btn-primary' : 'btn-outline-secondary'"
                  @click="trendMetric = 'value'"
                >Revenue</button>
                <button
                  type="button"
                  class="btn"
                  :class="trendMetric === 'profit' ? 'btn-primary' : 'btn-outline-secondary'"
                  @click="trendMetric = 'profit'"
                >Profit</button>
              </div>
            </div>
            <dashboard-saleschart
              :chart-data="activeTrendChartData"
              :chart-options="trendChartOptions"
              :height="140"
            />
          </div>

          <!-- Sold-by & Table row -->
          <div class="row">
            <!-- Sold-by breakdown -->
            <div v-if="soldByBreakdown.length > 0" class="col-md-4 col-sm-12 mb-3">
              <strong class="d-block mb-2">Sold By</strong>
              <table class="table table-sm table-bordered">
                <thead class="thead-light">
                  <tr>
                    <th>Staff</th>
                    <th class="text-right">Qty</th>
                    <th class="text-right">Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in soldByBreakdown" :key="row.name">
                    <td>{{ row.name }}</td>
                    <td class="text-right">{{ row.qty }}</td>
                    <td class="text-right">{{ convertCurrencyToShillings(row.value) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Movements table -->
            <div :class="soldByBreakdown.length > 0 ? 'col-md-8 col-sm-12' : 'col-12'">
              <b-table
                striped
                class="text-primary"
                hover
                small
                sticky-header="60vh"
                :items="items"
                :fields="fields"
                sort-icon-left
                responsive="sm"
                @row-clicked="handleItemClick"
              >
              </b-table>
            </div>
          </div>
        </div>
        <div v-else>
          <div class="mt-5 text-center text-danger">
            <p>Seems there hasn't been any inventory changes for the period.</p>
            <p>You can filter by dates above!</p>
          </div>
        </div>
      </div>
      <div v-else class="card">
        <div class="card-body text-center">
          <div class="spinner-border" role="status"></div>
          <p>Loading...</p>
        </div>
      </div>
    </div>

    <!-- Product Changes Tab -->
    <div v-if="activeTab === 'changes'" style="margin-bottom: 5em;">
      <div v-if="changesLoaded">
        <div v-if="changeItems.length > 0">
          <hr />

          <div class="timeline">
            <div
              v-for="log in changeItems"
              :key="log.id"
              class="timeline-item d-flex mb-3"
            >
              <div class="timeline-dot mr-3 flex-shrink-0">
                <div
                  class="rounded-circle d-flex align-items-center justify-content-center bg-primary text-white"
                  style="width:32px;height:32px;"
                >
                  <i class="bi bi-pencil" style="font-size:.75rem;"></i>
                </div>
              </div>

              <div class="flex-grow-1 border-bottom pb-2" style="min-width:0;">
                <div class="d-flex justify-content-between align-items-start">
                  <span
                    class="font-weight-bold text-primary"
                    style="cursor:pointer;"
                    @click="handleItemClick(log)"
                  >{{ log.product_name }}</span>
                  <small class="text-secondary flex-shrink-0 ml-2">{{ log.user }} · {{ log.created_at }}</small>
                </div>

                <div class="mt-1">
                  <div v-for="change in log.changes" :key="change.field" class="change-row">
                    <span class="font-weight-bold">{{ change.label }}:</span>
                    <template v-if="change.kind === 'set'">
                      set to <span class="text-success font-weight-bold">{{ formatChangeValue(change.new) }}</span>
                    </template>
                    <template v-else-if="change.kind === 'cleared'">
                      <span class="text-danger">cleared</span> (was {{ formatChangeValue(change.old) }})
                    </template>
                    <template v-else-if="change.kind === 'updated'">
                      updated
                    </template>
                    <template v-else>
                      <span class="text-danger">{{ formatChangeValue(change.old) }}</span>
                      <i class="bi bi-arrow-right mx-1"></i>
                      <span class="text-success font-weight-bold">{{ formatChangeValue(change.new) }}</span>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-between align-items-center mt-2">
            <small class="text-muted">Page {{ changesMeta.current_page }}</small>
            <div>
              <button
                class="btn btn-sm btn-outline-secondary mr-1"
                :disabled="!changesMeta.has_prev"
                @click="fetchProductChanges(changesMeta.current_page - 1)"
              >Prev</button>
              <button
                class="btn btn-sm btn-outline-secondary"
                :disabled="!changesMeta.has_next"
                @click="fetchProductChanges(changesMeta.current_page + 1)"
              >Next</button>
            </div>
          </div>
        </div>
        <div v-else-if="changesError">
          <div class="mt-5 text-center text-danger">
            <p>{{ changesError }}</p>
            <button class="btn btn-sm btn-outline-primary" @click="fetchProductChanges(changesMeta.current_page)">Retry</button>
          </div>
        </div>
        <div v-else>
          <div class="mt-5 text-center text-danger">
            <p>No product changes found for the selected period.</p>
            <p>You can filter by dates above!</p>
          </div>
        </div>
      </div>
      <div v-else class="card">
        <div class="card-body text-center">
          <div class="spinner-border" role="status"></div>
          <p>Loading...</p>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import DateRangePicker from "vue2-daterange-picker/src/components/DateRangePicker";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import moment from "moment";

export default {
  name: "InventoryActivity",
  components: { DateRangePicker },
  data() {
    return {
      activeTab: "movements",
      // Stock movements
      movementsLoaded: false,
      allSoldQty: 0,
      allSoldValue: 0,
      allSoldProfit: 0,
      avgSellingPrice: 0,
      items: [],
      fields: [
        { key: "item", sortable: true },
        { key: "activity", sortable: false },
        { key: "done_by", sortable: true },
        { key: "date", sortable: true },
      ],
      // Trend chart
      hasSalesGraph: false,
      trendMetric: "qty",
      trendChartsByMetric: { qty: null, value: null, profit: null },
      trendChartOptions: {
        responsive: true,
        plugins: {
          legend: { display: false },
          tooltip: { mode: "index", intersect: false },
        },
        scales: {
          y: { beginAtZero: true, ticks: { precision: 2 } },
        },
      },
      // Sold-by
      soldByBreakdown: [],
      // Product changes
      changesLoaded: false,
      changesError: null,
      changeItems: [],
      changesMeta: { current_page: 1, has_next: false, has_prev: false },
      _changesPending: null,
      // Shared filters
      filters: {
        search: "",
        activity: "",
      },
      dateRange: { startDate: null, endDate: null },
      autoApply: true,
      append_to_body: true,
      datepicker_opens: "right",
      ranges: false,
    };
  },
  computed: {
    activeTrendChartData() {
      return this.trendChartsByMetric[this.trendMetric];
    },
  },
  watch: {
    dateRange: {
      deep: true,
      handler() {
        const urlParams = new URLSearchParams(window.location.search);
        if (this.dateRange.startDate && this.dateRange.endDate) {
          urlParams.set("start-date", this.dateRange.startDate.toISOString().split("T")[0]);
          urlParams.set("end-date", this.dateRange.endDate.toISOString().split("T")[0]);
        }
        window.history.replaceState(null, null, "?" + urlParams.toString());
      },
    },
    activeTab(tab) {
      if (tab === "movements" && !this.movementsLoaded) {
        this.getInventoryActivity();
      } else if (tab === "changes" && !this.changesLoaded) {
        this.fetchProductChanges();
      }
    },
  },
  mounted() {
    this.getInventoryActivity();
  },
  methods: {
    formatDate(date, format) {
      return moment(date).format(format);
    },
    convertCurrencyToShillings(value) {
      return new Intl.NumberFormat("en-KE", {
        style: "currency",
        currency: "KES",
      }).format(value);
    },
    formatMovementActivity(raw) {
      if (!raw) return raw;
      // Legacy rows from before product edits stopped dumping every
      // submitted field as "field - value,field2 - value2,..." here (that
      // dump now only goes to the Product Changes tab, properly labeled).
      // Detect the old shape for display only - the stored data isn't
      // touched - and re-render it with the same field labels.
      const parts = raw.split(",").map((p) => p.trim()).filter(Boolean);
      if (parts.length < 3) return raw;

      const fieldPattern = /^[a-z_]+ -/;
      const matchingParts = parts.filter((p) => fieldPattern.test(p));
      // movement_type is a VARCHAR(255) - the old implode() could exceed
      // that and get silently cut off mid-word, leaving one trailing
      // fragment with no " -" at all (e.g. "...,affects_inv"). Tolerate
      // exactly that one truncated fragment rather than bailing out and
      // showing the raw dump for every long legacy row.
      const looksLikeFieldDump = matchingParts.length >= 3 && matchingParts.length >= parts.length - 1;
      if (!looksLikeFieldDump) return raw;

      const labels = {
        product: "Product Name", productcat_id: "Category", ppu: "Selling Price",
        wholesale_ppu: "Wholesale Buying Price", wholesale_sp: "Wholesale Selling Price",
        sku: "SKU", remaining_units: "Stock Units", measuring_unit: "Unit of Measure",
        minimum_stock: "Minimum Stock", low_stock: "Low Stock Threshold",
        is_service: "Is Service", affects_profit: "Affects Profit",
        affects_inventory: "Affects Inventory", can_be_sold: "Can Be Sold",
        is_reservable: "Is Reservable", is_directly_proportional: "Directly Proportional",
        expiry_date: "Expiry Date", discount: "Discount", commission: "Commission",
        bg_color: "Color",
      };

      const fields = matchingParts.map((p) => {
        const field = p.split(/\s*-\s*/)[0].trim();
        return labels[field] || field.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
      });

      return `Product edited — ${fields.join(", ")}`;
    },
    formatChangeValue(value) {
      // Only strict booleans - a numeric 0 (e.g. stock going to 0) must
      // stay "0", not get misread as a false/"No" flag.
      if (value === null || value === undefined || value === "") return "—";
      if (value === true) return "Yes";
      if (value === false) return "No";
      return value;
    },
    applyFilters() {
      if (this.activeTab === "movements") {
        this.getInventoryActivity();
      } else {
        this.fetchProductChanges(1);
      }
    },
    handleItemClick(item) {
      const url = new URL(window.location.href);
      const searchParams = url.searchParams;
      if (!searchParams.has("p-identifier-id")) {
        searchParams.set("p-identifier-id", item.product_id);
        url.search = searchParams.toString();
        window.location.href = url.toString();
      }
    },
    _buildTrendCharts(rawMovements) {
      // Group by calendar day
      const byDay = {};
      rawMovements.forEach((item) => {
        const day = moment(item.created_at).format("DD MMM");
        if (!byDay[day]) byDay[day] = { qty: 0, value: 0, profit: 0 };
        byDay[day].qty   += item.qty;
        byDay[day].value += item.qty * item.selling_price;
        byDay[day].profit += item.profit;
      });

      const labels = Object.keys(byDay);
      const baseDataset = {
        fill: true,
        tension: 0.35,
        pointRadius: labels.length > 30 ? 2 : 4,
        pointHoverRadius: 6,
        borderWidth: 2,
      };

      this.trendChartsByMetric = {
        qty: {
          labels,
          datasets: [{
            ...baseDataset,
            label: "Units Sold",
            borderColor: "#fd4729",
            backgroundColor: "rgba(253,71,41,0.08)",
            data: labels.map((d) => byDay[d].qty),
          }],
        },
        value: {
          labels,
          datasets: [{
            ...baseDataset,
            label: "Revenue (KES)",
            borderColor: "#28a745",
            backgroundColor: "rgba(40,167,69,0.08)",
            data: labels.map((d) => byDay[d].value),
          }],
        },
        profit: {
          labels,
          datasets: [{
            ...baseDataset,
            label: "Profit (KES)",
            borderColor: "#007bff",
            backgroundColor: "rgba(0,123,255,0.08)",
            data: labels.map((d) => byDay[d].profit),
          }],
        },
      };

      this.hasSalesGraph = labels.length > 0;
    },
    _buildSoldBy(rawMovements) {
      const byUser = {};
      rawMovements.forEach((item) => {
        const name = item.user?.name || "Unknown";
        if (!byUser[name]) byUser[name] = { qty: 0, value: 0 };
        byUser[name].qty   += item.qty;
        byUser[name].value += item.qty * item.selling_price;
      });

      this.soldByBreakdown = Object.entries(byUser)
        .map(([name, d]) => ({ name, qty: +d.qty.toFixed(2), value: d.value }))
        .sort((a, b) => b.value - a.value);
    },
    getInventoryActivity() {
      this.movementsLoaded = false;
      this.hasSalesGraph = false;
      this.soldByBreakdown = [];

      const urlParams = new URLSearchParams(window.location.search);
      const params = {
        startdate: this.dateRange.startDate ? this.dateRange.startDate.toISOString().split("T")[0] : null,
        enddate: this.dateRange.endDate ? this.dateRange.endDate.toISOString().split("T")[0] : null,
        onlysales: urlParams.get("only-sales") || null,
        product_id: urlParams.get("p-identifier-id") || false,
        search: this.filters.search || null,
        activity: this.filters.activity || null,
      };

      axios
        .get("/api/inventory-activity", { params })
        .then((result) => {
          let prodarray = [];

          const isSalesView = !!result.data.productMovement;
          const data = result.data.productMovement || result.data;

          data.forEach((value) => {
            if (typeof value === "object") {
              const searchText = this.filters.search.toLowerCase();
              const activityFilter = this.filters.activity.toLowerCase();
              const itemName = (value.product?.product || "").toLowerCase();
              const activityText = (value.movement_type || "").toLowerCase();

              if (
                (!searchText || itemName.includes(searchText) || activityText.includes(searchText)) &&
                (!activityFilter || activityText.includes(activityFilter))
              ) {
                prodarray.push({
                  id: value.id,
                  activity: this.formatMovementActivity(value.movement_type),
                  product_id: value.product_id,
                  done_by: value.user?.name || "",
                  item: value.product?.product || "",
                  date: moment(value.created_at).format("HH:mm DD-MM-YYYY"),
                });
              }
            }
          });

          this.items = prodarray;
          this.allSoldQty   = result.data.totalQty   || 0;
          this.allSoldValue = result.data.totalValue  || 0;
          this.allSoldProfit = result.data.totalProfit || 0;
          this.avgSellingPrice = this.allSoldQty > 0 ? this.allSoldValue / this.allSoldQty : 0;

          if (isSalesView && data.length > 0) {
            this._buildTrendCharts(result.data.productMovement);
            this._buildSoldBy(result.data.productMovement);
          }

          this.movementsLoaded = true;
        })
        .catch((err) => {
          console.error(err);
          this.movementsLoaded = true;
        });
    },
    fetchProductChanges(page = 1) {
      if (this._changesPending) {
        this._changesPending.abort();
      }
      const controller = new AbortController();
      this._changesPending = controller;

      this.changesLoaded = false;
      this.changesError = null;

      const urlParams = new URLSearchParams(window.location.search);
      const params = {
        startdate: this.dateRange.startDate ? this.dateRange.startDate.toISOString().split("T")[0] : null,
        enddate: this.dateRange.endDate ? this.dateRange.endDate.toISOString().split("T")[0] : null,
        product_id: urlParams.get("p-identifier-id") || null,
        search: this.filters.search || null,
        page,
      };

      axios
        // Without a timeout, a hung request (server or network) leaves this
        // spinning forever with no feedback - reported as "never loads".
        .get("/api/product-change-logs", { params, signal: controller.signal, timeout: 20000 })
        .then((result) => {
          const data = result.data;
          this.changeItems = (data.data || []).map((item) => ({
            ...item,
            created_at: moment(item.created_at).format("HH:mm DD-MM-YYYY"),
          }));
          this.changesMeta = {
            current_page: data.current_page || 1,
            has_next: !!data.next_page_url,
            has_prev: (data.current_page || 1) > 1,
          };
          this.changesLoaded = true;
        })
        .catch((err) => {
          if (err.name === "CanceledError" || err.name === "AbortError" || err.code === "ERR_CANCELED") return;
          console.error(err);
          this.changesError = err.code === "ECONNABORTED"
            ? "This is taking too long to load — try narrowing the date range."
            : "Couldn't load product changes. Please try again.";
          this.changesLoaded = true;
        });
    },
  },
};
</script>

<style scoped>
.nav-tabs .nav-link {
  color: #6c757d;
}
.nav-tabs .nav-link:hover {
  color: #fd4729;
  border-color: transparent transparent #dee2e6;
}
.nav-tabs .nav-link.active {
  color: #fd4729;
  border-top: 2px solid #fd4729;
  border-left-color: #dee2e6;
  border-right-color: #dee2e6;
  border-bottom-color: #fff;
}
.timeline-dot {
  padding-top: 2px;
}
.change-row {
  font-size: 0.85rem;
  color: #495057;
}
</style>
